import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { API_ERROR_CODE } from "@store/constants";
import GridItem, { GridItemProps } from "@store/MobiusOverrides/GridItem/GridItem";
import { PaymentHistoryPageContext } from "@store/Pages/PaymentHistoryPage";
import { getShowNormalPrice, getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import { PaymentHistoryDataViewContext } from "@store/Store/Data/Payment/PaymentsSelectors";
import updateSearchFields from "@store/Store/Pages/PaymentHistory/Handlers/UpdateSearchFields";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { linkStyles } from "@store/theme/common/mixins";
import { tableGridProps } from "@store/theme/common/props/tableGrid";
import { TYPOGRAPHY } from "@store/theme/design";
import { PaymentModel } from "@store/Types/PaymentHistory";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import getDateTimeFormat from "@store/utilities/getDateTimeFormat";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import PaymentHistoryInvoicesTable from "@store/Widgets/PaymentHistory/PaymentHistoryInvoicesTable";
import * as React from "react";
import { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const Pagination = getWidgetComponent("PaymentHistory/PaymentHistoryPagination");

const mapStateToProps = (state: StoreApplicationState) => ({
    getPaymentHistoryParameter: state.store.pages.paymentHistory.getPaymentHistoryParameter,
    language: state.context.session.language,
    showNormalPrice: getShowNormalPrice(state),
    dateFormatDisplay: getStoreSettingsCollection(state).poolcorpSettings.dateFormatDisplay,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PaymentHistoryTableStyles {
    buttonOpenInvoices?: ButtonPresentationProps;
    mobileButtonOpenInvoices?: LinkPresentationProps;
    centeringWrapper?: InjectableCss;
    checkAmountBodyGridCell?: GridItemProps;
    checkAmountGridHeader: GridItemProps;
    checkNumberBodyGridCell?: GridItemProps;
    mobileHeaderHidden?: HiddenProps;
    mobileHeader?: TypographyPresentationProps;
    checkNumberGridHeader: GridItemProps;
    container?: InjectableCss;
    headerText?: TypographyProps;
    invoicesModal?: ModalPresentationProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    resultCountWrapper?: InjectableCss;
    paymentCountText?: TypographyPresentationProps;
    postDateBodyGridCell?: GridItemProps;
    postDateGridHeader: GridItemProps;
    spinner?: LoadingSpinnerProps;
    tableGridBody?: GridContainerProps;
    tableGridHead?: GridContainerProps;
    tableGridRow?: GridItemProps;
    tableGridRowContainer?: GridContainerProps;
    wrapper?: InjectableCss;
}

export const paymentHistorytTableStyles: PaymentHistoryTableStyles = {
    container: tableGridProps.container,
    headerText: tableGridProps.textHeader,
    centeringWrapper: tableGridProps.centeringWrapper,
    spinner: tableGridProps.spinner,
    noResultsContainer: tableGridProps.noResultsContainer,
    noResultsText: tableGridProps.noResultsText,
    resultCountWrapper: {
        css: css`
            width: 100%;
            display:flex;
            justify-content: space-between;
            flex-wrap: wrap;
            align-items: start;
            border-bottom: 1px solid ${getColor("common.border")};
            margin-bottom: 0.5em;

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: [null, null, null, "none"], properties: ["border-bottom"] },
                        { values: [null, null, null, "0px"], properties: ["margin-bottom"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    paymentCountText: {
        forwardAs: "p",
        size: TYPOGRAPHY.size.smaller,
        css: css`
            margin-bottom: 14px;
        `,
    },
    invoicesModal: {
        sizeVariant: "large",
    },
    buttonOpenInvoices: {
        variant: "tertiary",
    },
    mobileButtonOpenInvoices: {
        css: css`
            ${linkStyles()}
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    checkAmountBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 6, 4, 4],
        css: css`
            ${tableGridProps.tableGridCell?.css}
            flex-direction: column;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, "flex-start"], properties: ["align-items"] }],
                    mediaOptions.max,
                )}
        `,
    },
    checkAmountGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 4, 4],
    },
    checkNumberBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 6, 4, 4],
    },
    mobileHeaderHidden: tableGridProps.mobileHeaderHidden,
    mobileHeader: {
        ...tableGridProps.mobileHeader,
        css: css`
            ${tableGridProps.mobileHeader?.css}
            display: block;
        `,
    },
    checkNumberGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 4, 4],
    },
    postDateBodyGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 6, 4, 4],
        css: css`
            ${tableGridProps.tableGridCell?.css}
            flex-direction: column;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, "flex-start"], properties: ["align-items"] }],
                    mediaOptions.max,
                )}
        `,
    },
    postDateGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 4, 4],
    },
    tableGridBody: {
        ...tableGridProps.tableGridBody,
        css: css`
            ${tableGridProps.tableGridBody?.css}
            font-size: ${TYPOGRAPHY.size.smaller};
        `,
    },
    tableGridHead: tableGridProps.tableGridHead,
    tableGridRowContainer: {
        ...tableGridProps.tableGridRowContainer,
        css: css`
            ${tableGridProps.tableGridRowContainer?.css}
            ${GridOffset} {
                margin: 0;
                align-items: center;
            }
        `,
    },
    tableGridRow: tableGridProps.tableGridRow,
};

const styles = paymentHistorytTableStyles;

const PaymentHistoryTable = (props: Props) => {
    // Sort not implemented yet on the back-end side.
    // const headerClick = (sortField: string) => {
    //     const sort = props.getPaymentHistoryParameter.sort === sortField ? `${sortField} DESC` : sortField;
    //     props.updateSearchFields({ sort });
    // };

    // const sorted = (sortField: string) => {
    //     let sorted: boolean | string = false;
    //     if (props.getPaymentHistoryParameter.sort === sortField) {
    //         sorted = "ascending";
    //     } else if (props.getPaymentHistoryParameter.sort === `${sortField} DESC`) {
    //         sorted = "descending";
    //     }
    //     return sorted as SortOrderOptions;
    // };

    const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false);
    const [paymentInModal, setPaymentInModal] = useState<PaymentModel>();
    const paymentHistoryDataView = useContext(PaymentHistoryDataViewContext);

    const openInvoices = (payment: PaymentModel) => {
        setPaymentInModal(payment);
        setIsInvoicesModalOpen(true);
    };
    const onInvoicesClose = () => {
        setIsInvoicesModalOpen(false);
    };

    if (!paymentHistoryDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (paymentHistoryDataView.properties?.apiErrorCode === API_ERROR_CODE.timeout) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>
                    {translate("The search has timed out, try again with more specific filters")}
                </Typography>
            </StyledWrapper>
        );
    }

    if (paymentHistoryDataView.value.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No payment found")}</Typography>
            </StyledWrapper>
        );
    }

    const rows = paymentHistoryDataView.value.map(payment => {
        return {
            payment,
            id: payment.id,
            checkNumber: payment.checkNumber,
            checkAmount: payment.checkAmount,
            postDate: payment.postDate
                ? getDateTimeFormat({
                    dateTime: new Date(payment.postDate),
                    dateFormat: props.dateFormatDisplay,
                })
                : "",
            invoiceDetails: payment.invoiceDetails,
        };
    });

    const paymentsCount =
        paymentHistoryDataView.value && paymentHistoryDataView.pagination
            ? paymentHistoryDataView.pagination.totalItemCount
            : 0;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.resultCountWrapper}>
                <Typography {...styles.paymentCountText}>
                    ({paymentsCount}) {translate(paymentsCount > 1 ? "Payments" : "Payment")}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.container}>
                <GridContainer {...styles.tableGridHead}>
                    <GridItem {...styles.checkNumberGridHeader}>
                        <Typography {...styles.headerText}>{translate("Check Number")}</Typography>
                    </GridItem>
                    <GridItem {...styles.checkAmountGridHeader}>
                        <Typography {...styles.headerText}>{translate("Check Amount")}</Typography>
                    </GridItem>
                    <GridItem {...styles.postDateGridHeader}>
                        <Typography {...styles.headerText}>{translate("Post Date")}</Typography>
                    </GridItem>
                </GridContainer>
                <GridContainer {...styles.tableGridBody}>
                    {rows.map(({ payment, id, checkNumber, checkAmount, postDate }) => {
                        return (
                            <GridItem {...styles.tableGridRow} key={id} data-test-selector="paymentHistory_invoiceLine">
                                <GridContainer {...styles.tableGridRowContainer}>
                                    <GridItem {...styles.checkNumberBodyGridCell}>
                                        <Hidden {...styles.mobileHeaderHidden}>
                                            <Typography {...styles.mobileHeader}>
                                                {translate("Check #")}
                                            </Typography>
                                            <Link 
                                                {...styles.mobileButtonOpenInvoices}
                                                onClick={() => {openInvoices(payment)}}
                                            >
                                                {checkNumber}
                                            </Link>
                                        </Hidden>
                                        <Hidden below="lg">
                                            <Button
                                                type="button"
                                                {...styles.buttonOpenInvoices}
                                                onClick={() => {
                                                    openInvoices(payment);
                                                }}
                                            >
                                                {checkNumber}
                                            </Button>
                                        </Hidden>
                                    </GridItem>
                                    <GridItem {...styles.checkAmountBodyGridCell}>
                                        <Hidden {...styles.mobileHeaderHidden}>
                                            <Typography {...styles.mobileHeader}>
                                                {translate("Check Amount")}
                                            </Typography>
                                        </Hidden>
                                        {checkAmount && <LocalizedCurrency amount={checkAmount} />}
                                    </GridItem>
                                    <GridItem {...styles.postDateBodyGridCell} data-test-selector="paymentHistory_paymentLine_date">
                                        <Hidden {...styles.mobileHeaderHidden}>
                                            <Typography {...styles.mobileHeader}>
                                                {translate("Post Date")}
                                            </Typography>
                                        </Hidden>
                                        <Typography>{postDate}</Typography>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        );
                    })}
                </GridContainer>
                <Modal
                    headline={translate("Invoices")}
                    {...styles.invoicesModal}
                    isOpen={isInvoicesModalOpen}
                    handleClose={() => onInvoicesClose()}
                >
                    <PaymentHistoryInvoicesTable payment={paymentInModal}></PaymentHistoryInvoicesTable>
                </Modal>
            </StyledWrapper>
            <Pagination />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PaymentHistoryTable),
    definition: {
        group: "Payment History" as any,
        displayName: "Search Results Table",
        allowedContexts: [PaymentHistoryPageContext] as any[],
    },
};

export default widgetModule;
